import React, { useContext, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDisclosure } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import RelativeTime from "dayjs/plugin/relativeTime";

// Components
import DashboardLayout from "./DashboardLayout";
import MapBox from "../Map/map";
import SingleUserPost from "../SingleUserPost/SingleUserPost";
import DashboardSide from "./DashboardSide";
import UserCard from "./UserSlider/UserCard";
import ModalComponent from "../Modal/Modal";

// Modals
import ProfileUpdateNotice from "../Modal";
import AccountModal from "../Modal/AccountModal";
import ReportModal from "../Modal/ReportModal";
import LoginModal from "../Modal/LoginModal";
import UploadEmergency from "../Upload/UploadEmergency";
import SharePost from "../Upload/SharePost";

// Icons
import { fullscreen_icon } from "../../constants/images";
import { CiZoomIn, CiZoomOut } from "react-icons/ci";

// Context & Socket
import { MapContext } from "../../context/MapContext";
import SocketManager from "../../socket/socketManager";

// Redux Actions
import { setIsLoading } from "../../redux/features/features.action";
import {
  getMapEvents,
  getUser,
  logOut,
  setModalStatus,
  setStatus,
} from "../../redux/auth/auth.actions";
import {
  closeSinglePost,
  fetchAllEvents,
  viewSinglePost,
} from "../../redux/posts/posts.action";

// Utils
import { getGoogleLocation } from "../../utils/utils";

// Styles
import "mapbox-gl/dist/mapbox-gl.css";

function DashboardHome() {
  // Redux state
  const dispatch = useDispatch();
  const darktheme = useSelector((state) => state.features.settings.darktheme);
  const token = useSelector((state) => state.auth.accessToken);
  const user = useSelector((state) => state.auth.user);
  const uploadModalStatus = useSelector((state) => state.auth.showUploadModal);
  const latestPost = useSelector((state) => state.posts.uploadData);
  const { postId, modalId, events, modalActive } = useSelector(
    (state) => state.posts
  );

  const mapEvents = useSelector((state) => state.auth.mapEvents);

  // Local state
  const [location, setLocation] = useState({ longitude: 0, latitude: 0 });
  const [stream, setStream] = useState(null);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [showAds, setShowAds] = useState(false);
  const [activePostId, setActivePostId] = useState("");
  const [reportModal, setReportModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [, setCount] = useState();
  const [, setEmergencyModalId] = useState(0);

  // Hooks
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const router = useLocation();
  const { mapRef } = useContext(MapContext);

  // Initialize dayjs plugin
  dayjs.extend(RelativeTime);

  // Fetch user location on mount
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const { lat, lng } = await getGoogleLocation();
        setLocation({
          latitude: lat,
          longitude: lng,
        });
      } catch (error) {
        console.error("Error fetching location:", error);
      }
    };

    fetchLocation();
    // eslint-disable-next-line
  }, []);

  // Set loading state on mount
  useEffect(() => {
    dispatch(setIsLoading(true));
    // eslint-disable-next-line
  }, []);

  // Connect socket and listen for live streams
  useEffect(() => {
    SocketManager?.instance?.socket?.connect();
    SocketManager?.instance?.emitListLiveStream({
      lat: location?.latitude,
      lon: location?.longitude,
    });
    SocketManager?.instance?.listenListLiveStream((data) => {
      setStream(data);
    });
    // eslint-disable-next-line
  }, []);

  // Check if profile is incomplete after 90 seconds
  useEffect(() => {
    if (
      (user?.phoneNumber === undefined ||
        user?.displayName === "" ||
        user?.dob === null) &&
      token
    ) {
      const intervalId = setTimeout(() => {
        onOpen();
        setProfileIncomplete(true);
      }, 90000);
      return () => clearInterval(intervalId);
    }
    // eslint-disable-next-line
  }, []);

  // Fetch events when route changes
  useEffect(() => {
    getEvents();
    // eslint-disable-next-line
  }, [dispatch, router.pathname]);

  // Fetch map events when location changes
  useEffect(() => {
    const { longitude, latitude } = location;
    dispatch(getMapEvents({ longitude, latitude }));
    // eslint-disable-next-line
  }, [location, dispatch, router.pathname]);

  // Fetch user data when user is available
  useEffect(() => {
    if (user) {
      dispatch(getUser());
    }
    // eslint-disable-next-line
  }, []);

  // Check if user is deleted and log out if needed
  useEffect(() => {
    if (user?.isDeleted) {
      navigate("/login");
      dispatch(setStatus(true));
      dispatch(logOut());
    }
    // eslint-disable-next-line
  }, [user]);

  // Handle emergency uploads route
  useEffect(() => {
    if (router.pathname === "/dashboard/events/uploads") {
      onOpen();
      setEmergencyModalId(1);
    }
    // eslint-disable-next-line
  }, []);

  // Show ads after some time
  useEffect(() => {
    setTimeout(() => setShowAds(!showAds), 100000);
    // eslint-disable-next-line
  }, []);

  // Event handlers
  const getEvents = (more) => {
    dispatch(fetchAllEvents(more));
  };

  const handleSinglePostModal = (post) => {
    if (token) {
      if (user.suspend) {
        setSuspended(true);
      } else {
        dispatch(
          viewSinglePost({
            postId: post?.id,
            modalId: 10,
            modalActive: isOpen,
          })
        );
        // Center map on post location
        mapRef.current.flyTo({
          center: [post.coords.longitude, post.coords.latitude],
          zoom: 15,
          speed: 0.8,
          essential: true,
        });
        setCount(post?.likeCount);
        getEvents();
      }
    } else {
      setShowModal(true);
    }
  };

  const closeSinglePostModal = () => {
    dispatch(closeSinglePost());
  };

  const reportPost = (id) => {
    setActivePostId(id);
    setReportModal(!reportModal);
  };

  const close = () => {
    onClose();
    dispatch(setModalStatus(0));
  };

  const updateModalId = (item) => {
    dispatch(setModalStatus(item));
    setEmergencyModalId(item);
    onOpen();
  };

  const getData = () => {
    // Placeholder for data refresh function
  };

  return (
    <DashboardLayout>
      <div
        className={`w-full flex ${
          darktheme ? "bg-kdark3 text-[#888a8b]" : "bg-[#fff] text-[#1C2024]"
        }`}
      >
        {/* Map container */}
        <div className="w-full fixed bottom-0 top-0">
          <div className="relative w-full">
            <div className="w-full">
              {mapEvents && (
                <MapBox
                  mapEvents={events}
                  user={user}
                  token={token}
                  handleSinglePost={handleSinglePostModal}
                />
              )}
            </div>

            {/* Map controls - fullscreen, zoom in and out */}
            <div className="absolute right-3 bottom-3 flex">
              <div className="flex relative bg-[#F9FAFB] px-[18px] h-[40px] items-center rounded-[26px]">
                <img
                  src={fullscreen_icon}
                  alt="icon"
                  className="w-[18px] h-[18px]"
                />
                <span className="text-[14px] ml-[13px]">Fullscreen</span>
              </div>
              <div className="flex relative ml-[9px] bg-[#F9FAFB] px-[18px] h-[40px] items-center rounded-[26px]">
                <div>
                  <CiZoomIn size={18} />
                </div>
                <div className="w-[3px] h-[40px] mx-[13px] bg-[#F3F5F7]"></div>
                <div>
                  <CiZoomOut size={18} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div
          className={`w-full md:w-[386px] absolute h-[100vh] right-0 ${
            darktheme
              ? "bg-kdark3 text-[#F9FAFB]"
              : "bg-[#fff] text-[#1C2024]"
          }`}
          id="scrollableDiv"
          style={{ overflowY: "scroll" }}
        >
          {/* Stream section */}
          {stream && (
            <div className="w-full px-[2rem] my-[1rem]">
              <UserCard item={stream} />
            </div>
          )}
          
          {/* Navigation tabs */}
          <div
            className={` ${darktheme ? "text-[#afafaf]" : "border-b-[#F2F4F7]"}  flex border-b-[1px] ${
              stream ? "" : "pt-5"
            } `}
          >
            <div className="w-[50%]">
              <p className={`mx-auto text-center ${darktheme ? "text-[#F9FAFB]" : "text-[#121212]"} font-extrabold text-sm w-20 border-b-[red] border-b-[1px]`}>
                Home
              </p>
            </div>
            <div className="w-[50%]">
              <p className={`mx-auto text-center ${darktheme ? "text-[#777c87]" : "text-[#1C2024]"} w-20 text-sm font-normal`}>
                Following
              </p>
            </div>
          </div>
          
          {/* Posts section */}

          <DashboardSide
            events={events}
            user={user}
            token={token}
            openReportModal={reportPost}
            callEvents={() => getEvents(true)}
            handleSinglePost={handleSinglePostModal}
            updateCounts={getData}
            isOpen={isOpen}
            onClose={onClose}
            coords={location}
          />
        </div>
      </div>

      {/* Modals */}

      {suspended && (
        <AccountModal
          isOpen={suspended}
          onClose={() => setSuspended(false)}
          title={"Your account has been Suspended"}
          description="Figma ipsum component variant main layer. Flows star follower
            figma shadow connection polygon flows. Effect figjam ellipse union
            draft bullet figjam plugin star. Horizontal draft scrolling."
          type="Suspended"
        />
      )}

      {profileIncomplete && (
        <ProfileUpdateNotice
          user={user?.username}
          isOpen={isOpen}
          onClose={onClose}
        />
      )}

      {modalId === 10 && token && (
        <SingleUserPost
          openReportModal={reportPost}
          updateCounts={getData}
          handle={() => handleSinglePostModal()}
          theme={darktheme}
          id={postId}
          isOpen={modalActive && modalId === 10}
          onClose={closeSinglePostModal}
          coords={location}
        />
      )}

      {reportModal && (
        <ReportModal
          id={activePostId}
          isOpen={reportModal}
          onClose={reportPost}
        />
      )}
      {/* Emergency upload modal group */}
      {(uploadModalStatus >= 60 && uploadModalStatus <= 66) && (
        <UploadEmergency
          isOpen={uploadModalStatus === 60 && isOpen}
          getEvent={() => getEvents(true)}
          onClose={close}
          updateModal={updateModalId}
          type={"My"}
        />
      )}
      {/* Share post modal */}
      <ModalComponent
        isOpen={uploadModalStatus === 3 && isOpen}
        onClose={onClose}
        className="rounded-2xl"
        size={"sm"}
      >
        <SharePost
          theme={darktheme}
          id={latestPost?.id}
          header="Uploaded Successfully"
          title={latestPost?.title}
        />
      </ModalComponent>

      {/* Login modal */}

      {showModal && (
        <LoginModal isOpen={showModal} setShowModal={setShowModal} />
      )}
    </DashboardLayout>
  );
}

export default DashboardHome;
